import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Home, Plus, Folder } from "lucide-react";
import { api, type GitHubStatus } from "@/lib/api";
import type { Project } from "@/lib/api";
import { DaydraLogo } from "@/components/DaydraLogo";
import { cn } from "@/lib/utils";
import { GitHubAccountsSection } from "./sidebar/GitHubAccountsSection";
import { NewProjectForm } from "./sidebar/NewProjectForm";

interface HeaderMenuProps {
  open: boolean;
  onClose: () => void;
  currentProject: Project | null;
  onGoHome: () => void;
  onProjectRefresh?: () => void;
  onSwitchProject?: (projectId: string) => void;
}

const SLIDE_IN  = 300;
const SLIDE_OUT = 200;

export function HeaderMenu({
  open,
  onClose,
  currentProject,
  onGoHome,
  onProjectRefresh,
  onSwitchProject,
}: HeaderMenuProps) {
  const [closing, setClosing] = useState(false);
  const [githubStatus, setGithubStatus] = useState<GitHubStatus | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const closingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);

    setTimeout(() => {
      closingRef.current = false;
      setClosing(false);
      onClose();
    }, SLIDE_OUT);
  }, [onClose]);

  // Load GitHub status and projects when sidebar opens
  useEffect(() => {
    if (!open) return;
    api.github.status().then(setGithubStatus).catch(() => {});
    api.projects.list().then(setProjects).catch(() => {});
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, handleClose]);

  if (!open) return null;

  const isProject = !!currentProject;

  function goHome() {
    onGoHome();
    handleClose();
  }

  function handleProjectRefresh() {
    onProjectRefresh?.();
    // Refresh GitHub status too
    api.github.status().then(setGithubStatus).catch(() => {});
  }

  const sidebarContent = (
    <div
      className={cn(
        "flex flex-col h-full",
        "w-full",
        "md:w-72",
        "bg-[hsla(var(--surface-hue),var(--surface-sat),12%,0.55)] backdrop-blur-md backdrop-saturate-150",
        "md:border-r md:border-white/[0.06]",
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-5 sm:px-6 py-3 pt-4 sm:pt-3 shrink-0 bg-[hsla(var(--surface-hue),var(--surface-sat),9%,0.7)] backdrop-blur-md shadow-[0_1px_12px_rgba(0,0,0,0.3)]">
        <DaydraLogo size={28} className="shrink-0 text-white/50" />
        <div className="flex-1" />
        <button
          onClick={handleClose}
          className="flex items-center justify-center h-7 w-7 rounded-lg text-daydra-dim hover:text-daydra-text hover:bg-white/[0.06] transition-colors duration-fast shrink-0"
          aria-label="Close menu"
        >
          <X size={16} />
        </button>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-5">
        {/* Projects list */}
        {projects.length > 0 && (
          <section>
            <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
              Projects
            </h3>
            <div className="space-y-0.5">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    if (p.id !== currentProject?.id) {
                      onSwitchProject?.(p.id);
                    }
                    handleClose();
                  }}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-xs transition-colors duration-fast",
                    p.id === currentProject?.id
                      ? "text-daydra-accent bg-white/[0.06]"
                      : "text-daydra-muted hover:text-daydra-text hover:bg-white/[0.06]",
                  )}
                >
                  <Folder size={13} className="shrink-0" />
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* New project — always available when GitHub is connected */}
        {(githubStatus?.installations?.length ?? 0) > 0 && (
          <section>
            <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
              New Project
            </h3>
            <div className="rounded-xl p-3.5 bg-white/[0.04] border border-white/[0.06]">
              {showNewProject ? (
                <NewProjectForm
                  installations={githubStatus!.installations}
                  onCreated={(projectId) => {
                    setShowNewProject(false);
                    // Switch project first, then close sidebar
                    onSwitchProject?.(projectId);
                    handleClose();
                  }}
                  onCancel={() => setShowNewProject(false)}
                />
              ) : (
                <button
                  onClick={() => setShowNewProject(true)}
                  className="flex items-center gap-2 w-full text-xs text-daydra-muted hover:text-daydra-accent transition-colors duration-fast"
                >
                  <Plus size={13} />
                  <span>New project</span>
                </button>
              )}
            </div>
          </section>
        )}

        {/* GitHub accounts */}
        <GitHubAccountsSection
          installations={githubStatus?.installations ?? []}
          onInstallationsChange={(updated) => {
            setGithubStatus((prev) =>
              prev
                ? { ...prev, connected: updated.length > 0, installations: updated }
                : { connected: updated.length > 0, installations: updated },
            );
          }}
          onProjectRefresh={handleProjectRefresh}
          onClose={handleClose}
        />

      </div>

      {/* ── Footer ── */}
      {isProject && (
        <div className="px-4 py-3 border-t border-white/[0.04] shrink-0">
          <button
            onClick={goHome}
            className="flex items-center gap-2 w-full px-2 py-2 rounded-lg text-xs text-daydra-muted hover:text-daydra-text hover:bg-white/[0.06] transition-colors duration-fast"
          >
            <Home size={13} />
            <span>Back to Home</span>
          </button>
        </div>
      )}
    </div>
  );

  // ── Mobile: full-screen overlay via portal ──
  const mobileOverlay = createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
        style={{
          animation: closing
            ? `fadeOut ${SLIDE_OUT}ms ease-in forwards`
            : `fadeIn ${SLIDE_IN}ms ease-out both`,
        }}
      />
      <div
        className={cn(
          "relative z-10 h-full",
          closing ? "animate-slide-out-left" : "animate-slide-in-left",
        )}
      >
        {sidebarContent}
      </div>
    </div>,
    document.body,
  );

  // ── Desktop: inline sidebar ──
  const desktopSidebar = (
    <div
      className={cn(
        "hidden md:flex h-full shrink-0 overflow-hidden",
        closing
          ? "animate-sidebar-collapse"
          : "animate-sidebar-expand",
      )}
    >
      {sidebarContent}
    </div>
  );

  return (
    <>
      {mobileOverlay}
      {desktopSidebar}
    </>
  );
}
